import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/use-theme";

const mainNavItems = [
  { label: "Whisper", path: "/" },
  { label: "Psst...", path: "/staff-favorites" },
  { label: "Our Story", path: "/about" },
  { label: "Submit a Book", path: "/submit" },
];

const partnerItems = [
  { label: "For Influencers", path: "/partner/influencer" },
  { label: "For Bookstores", path: "/partner/bookstore" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isPartnerActive = partnerItems.some((item) => location.pathname === item.path);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setPartnerOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setPartnerOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <nav className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-lg">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="Shhh... Book Club" className="h-10 w-10 rounded-lg object-contain" />
          <span className="font-display text-lg font-extrabold tracking-tight text-foreground">
            Shhh... Book Club
          </span>
        </Link>

        {/* Desktop links */}
        <div className="hidden items-center gap-1 md:flex">
          {mainNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
                location.pathname === item.path
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              {item.label}
            </Link>
          ))}

          {/* Partner dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setPartnerOpen(!partnerOpen)}
              className={cn(
                "flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
                isPartnerActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              Partner With Us
              <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", partnerOpen && "rotate-180")} />
            </button>
            {partnerOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 overflow-hidden rounded-xl border border-border/50 bg-popover shadow-lg">
                {partnerItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "block px-4 py-2.5 text-sm font-medium transition-colors",
                      location.pathname === item.path
                        ? "bg-primary/10 text-primary"
                        : "text-popover-foreground hover:bg-muted"
                    )}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={toggleTheme}
            className="ml-2 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Toggle dark mode"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className="rounded-lg p-2 text-foreground md:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-border/50 bg-background px-4 pb-4 md:hidden">
          {mainNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "block rounded-lg px-4 py-3 text-sm font-semibold transition-colors",
                location.pathname === item.path
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              {item.label}
            </Link>
          ))}

          {/* Partner section in mobile */}
          <div className="mt-1 border-t border-border/30 pt-1">
            <span className="block px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground/60">
              Partner With Us
            </span>
            {partnerItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "block rounded-lg px-4 py-3 text-sm font-semibold transition-colors",
                  location.pathname === item.path
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <button
            onClick={toggleTheme}
            className="flex w-full items-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
        </div>
      )}
    </nav>
  );
}
